import React, { useState, useCallback, useMemo, MouseEvent } from 'react';
import { TUseTipsByDefault } from 'types';
import { Radio } from 'antd';
import { Rule } from 'antd/lib/form';
import { useTranslation } from 'react-i18next';
import Text from 'components/Text/Text';
import FormInput from 'components/Input/FormInput';
import classNames from 'classnames';
import style from './TipsField.module.scss';

interface ITipsField {
	setTips: (value: null | number) => void;
	setDefaultTips: (value: TUseTipsByDefault) => void;
	defaultTips: TUseTipsByDefault;
	tipsNumber: '0' | '1' | '2' | '3';
	isDisabled: boolean;
}

export const TipsField: React.FC<ITipsField> = React.memo(
	({ setTips, setDefaultTips, defaultTips, tipsNumber, isDisabled }) => {
		const [hasError, setHasError] = useState(false);
		const radioClasses = classNames(style.radio, { [style.hasError]: hasError });
		const { t } = useTranslation('error');

		const validationRules = useMemo<Rule[]>(
			() => [
				({ getFieldsValue }) => ({
					validator(_, value) {
						const otherTips = Object.values(getFieldsValue(['tips0', 'tips1', 'tips2', 'tips3'])).filter(
							(tips, i) => tips != null && i !== Number(tipsNumber),
						);

						if (value != null && otherTips.includes(value)) {
							setHasError(true);
							return Promise.reject(new Error(t('thisValueAlreadyExistsInAnotherTips')));
						}
						setHasError(false);
						return Promise.resolve();
					},
				}),
			],
			[t, tipsNumber],
		);

		const handleInputChange = useCallback(
			(value: number | null) => {
				setTips(value);
			},
			[setTips],
		);

		const handleRadioChange = useCallback(
			(e: MouseEvent<HTMLInputElement>) => {
				setDefaultTips(e.currentTarget.value as TUseTipsByDefault);
			},
			[setDefaultTips],
		);

		return (
			<div className={style.container}>
				<FormInput
					testId={`Tips${tipsNumber}Field`}
					type="number"
					name={`tips${tipsNumber}`}
					label={defaultTips === tipsNumber ? `tips${tipsNumber}DefaultLabel` : `tips${tipsNumber}Label`}
					onChangeNumber={handleInputChange}
					min={0}
					max={100}
					precision={1}
					validationRules={validationRules}
					disabled={isDisabled}
				/>
				<Radio className={radioClasses} value={tipsNumber} onClick={handleRadioChange} disabled={isDisabled}>
					<Text translationNameSpace="form" translationKey="useByDefault" asFragment={true} />
				</Radio>
			</div>
		);
	},
);
